import React from 'react';
import { Link } from 'react-router-dom';
import { categories } from '../context/ProductContext';
import { FaFacebook, FaWhatsapp, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';

const InstagramIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm5.6-.9a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z" />
  </svg>
);

const Footer: React.FC = () => {
  const handleWhatsAppClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const message =
      "Hello! I'm contacting you about your dress collection. Could you please share the latest available designs, sizes, and prices?";

    await Swal.fire({
      icon: 'info',
      title: 'Redirecting to WhatsApp',
      text: 'Opening WhatsApp chat in a new tab...',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1400,
      timerProgressBar: true,
    });

    const whatsappUrl = `https://wa.me/8801559539949?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="bg-secondary text-white mt-20">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity group">
              <div className="w-16 h-16 bg-white rounded-xl p-2 shadow-medium flex items-center justify-center group-hover:scale-105 transition-transform">
                <img src="/logo/fusionmartlogo.png" alt="Fusion Mart CHT Logo" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">FUSION MART CHT</h1>
                <p className="text-xs text-gray-400">Your Style Destination</p>
              </div>
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Your one-stop shop for trendy clothing and accessories. Quality products at affordable prices.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://www.facebook.com/fusionmart.cht" 
                className="w-10 h-10 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebook className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/8801559539949"
                className="w-10 h-10 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
                aria-label="WhatsApp: 01559539949"
                title="WhatsApp"
              >
                <FaWhatsapp className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/fusion_mart.cht"
                className="w-10 h-10 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                title="Instagram"
              >
                <InstagramIcon className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h1 className="text-lg font-display font-semibold mb-4 text-white">Quick Links</h1>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/shop" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  All Collections
                </Link>
              </li>
              {categories.map((category) => (
                <li key={category.id}>
                  <Link 
                    to={`/shop?category=${category.id}`}
                    className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    {category.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/about" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  About
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Customer Service */}
          <div>
            <h1 className="text-lg font-display font-semibold mb-4 text-white">Customer Service</h1>
            <ul className="space-y-3">
              <li>
                <Link to="/track-order" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Track Order
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/shipping" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Shipping Info
                </Link>
              </li>
              <li>
                <Link to="/returns" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Returns
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  FAQ
                </Link>
              </li>
              <li>
                <a 
                  href="https://maps.app.goo.gl/WdsrWQV6eNbCzrMq5" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors text-sm flex items-center gap-2 group"
                >
                  <span className="w-1.5 h-1.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                  Shop Location
                </a>
              </li>
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h1 className="text-lg font-display font-semibold mb-4 text-white">Get in Touch</h1>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <FaEnvelope className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">Email</p>
                  <a href="mailto:fusionmart.cht2019@gmail.com" className="text-white hover:text-primary transition-colors text-sm">
                    fusionmart.cht2019@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <FaPhoneAlt className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">Phone</p>
                  <a href="tel:+8801559539949" className="text-white hover:text-primary transition-colors text-sm">
                    01559539949
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <FaMapMarkerAlt className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">Address</p>
                  <p className="text-white text-sm leading-relaxed mb-2">
                    Jamtola(Anandanagar), Pankhaiya Para Road<br />
                    Khagrachari, Bangladesh
                  </p>
                  <a 
                    href="https://maps.app.goo.gl/WdsrWQV6eNbCzrMq5" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-light transition-colors text-sm inline-flex items-center gap-1"
                  >
                    <FaMapMarkerAlt className="w-3 h-3" />
                    View Location
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-300 text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} FUSION MART CHT. All rights reserved.
            </p>
            <p className="text-gray-400 text-xs text-center md:text-right">
              Developed by <Link to="/portfolio" className="text-primary hover:text-primary-light transition-colors font-medium">Malthas Chakma</Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
